import { FC, useState } from 'react';
import { Form, Typography } from 'antd';

import { FormItem, SubmitButton, TextField } from 'componets/form';
import { ModalComponent } from 'componets/common';

import { changePassword } from 'features/userSettings/actions';

import { Rules } from 'utils/constants';
import Utils from 'utils/utils';

import { FieldData, ModalProps } from 'models';
import { ChangePasswordProps } from 'models/api';

import { useAppDispatch, useTranslate } from 'hooks';

import s from './index.module.scss';

const PASSWORD_LENGTH = 8;

const PASSWORD_RULES = [Rules.LOWERCASE, Rules.UPPERCASE, Rules.NUMERIC, Rules.REQUIRED];

export const SettingsPasswordModal: FC<ModalProps> = ({ isOpen, onClose }) => {
	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		currentPasswordText,
		newPasswordText,
		confirmNewPasswordText,
		changePasswordText,
		changePasswordFormText,
	} = useTranslate([
		'currentPasswordText',
		'newPasswordText',
		'confirmNewPasswordText',
		'changePasswordText',
		'changePasswordFormText',
	]);

	const dispatch = useAppDispatch();

	const onFinish = (data: ChangePasswordProps) => {
		setIsSubmitting(true);
		dispatch(changePassword(data))
			.unwrap()
			.then(() => {
				onClose();
				form.resetFields();
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields);
				form.scrollToField(Object.keys(errors)[0]);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	return (
		<ModalComponent isOpen={isOpen} onClose={onClose} title={changePasswordText}>
			<Typography.Text className={s.textModal}>{changePasswordFormText}</Typography.Text>
			<Form
				form={form}
				scrollToFirstError
				onFinish={onFinish}
				onFieldsChange={setFields}
				fields={fields}
			>
				<FormItem
					validateTrigger="onSubmit"
					className={s.inputPassword}
					name="currentPass"
					rules={[Rules.REQUIRED]}
				>
					<TextField label={currentPasswordText} type="password" />
				</FormItem>

				<FormItem
					className={s.inputPassword}
					rules={[...PASSWORD_RULES, Rules.MIN_LENGTH('newPass', PASSWORD_LENGTH)]}
					name="newPass"
				>
					<TextField label={newPasswordText} type="password" />
				</FormItem>

				<FormItem
					className={s.inputPassword}
					rules={[
						...PASSWORD_RULES,
						Rules.MIN_LENGTH('confirmPass', PASSWORD_LENGTH),
						Rules.COMPARE_PASSWORDS('newPass'),
					]}
					name="confirmPass"
				>
					<TextField label={confirmNewPasswordText} type="password" />
				</FormItem>

				<SubmitButton text={changePasswordText} loading={isSubmitting} />
			</Form>
		</ModalComponent>
	);
};
